import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Lock, Mail, ArrowRight, Home } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

/** Dedicated Admin Login page — separate from the public /login flow.
 *  Only accepts users with role === "admin"; anyone else is rejected on the client. */
export default function AdminLogin() {
    const nav = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await login(email, password);
            if (!res?.ok) {
                toast.error(res?.error || "Invalid credentials");
                return;
            }
            if (res.user?.role !== "admin") {
                toast.error("This portal is for administrators only. Use the main login page.");
                localStorage.removeItem("gpm_token");
                window.location.href = "/login";
                return;
            }
            toast.success("Welcome back, admin.");
            nav("/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#000e21] via-[#001a3d] to-[#000e21] flex flex-col items-center justify-center px-5 py-10 relative overflow-hidden">
            <Seo title="Admin Login — Global Property Mart" description="Administrator sign-in portal." path="/admin/login" />

            {/* Ambient background flourishes */}
            <div className="absolute inset-0 grid-pattern opacity-25 pointer-events-none" />
            <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gold-500/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

            <Link
                to="/"
                data-testid="admin-login-home"
                className="text-on-image absolute top-6 left-6 text-xs text-white/50 hover:text-white inline-flex items-center gap-1.5"
            >
                <Home className="w-3.5 h-3.5" /> Back to site
            </Link>

            <div className="relative w-full max-w-md">
                <div className="text-on-image text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/15 border border-gold-500/40 mb-4">
                        <ShieldCheck className="w-3.5 h-3.5 text-gold-400" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold-300">Restricted Access</span>
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-white">Admin Portal</h1>
                    <p className="text-white/60 text-sm mt-2">Global Property Mart · staff sign-in only</p>
                </div>

                <form
                    onSubmit={onSubmit}
                    data-testid="admin-login-form"
                    className="rounded-2xl bg-white/[0.03] backdrop-blur-md border border-white/10 shadow-2xl p-6 sm:p-8 space-y-4"
                >
                    <label className="block">
                        <span className="text-xs text-white/60 font-semibold uppercase tracking-wider">Admin email</span>
                        <div className="relative mt-1.5">
                            <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="email"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="admin-login-email"
                                className="w-full bg-black/25 border border-white/15 rounded-md pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold-500/60 focus:outline-none"
                                placeholder="admin@globalpropertymart.in"
                            />
                        </div>
                    </label>

                    <label className="block">
                        <span className="text-xs text-white/60 font-semibold uppercase tracking-wider">Password</span>
                        <div className="relative mt-1.5">
                            <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="password"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="admin-login-password"
                                className="w-full bg-black/25 border border-white/15 rounded-md pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold-500/60 focus:outline-none"
                                placeholder="••••••••"
                            />
                        </div>
                    </label>

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit"
                        className="w-full mt-2 inline-flex items-center justify-center gap-2 py-3 rounded-md bg-gradient-to-r from-gold-500 to-amber-500 text-navy-900 font-extrabold text-sm hover:brightness-110 shadow-md disabled:opacity-60"
                    >
                        {submitting ? "Signing in…" : (<>Enter admin dashboard <ArrowRight className="w-4 h-4" /></>)}
                    </button>

                    <div className="pt-3 border-t border-white/5 flex items-center justify-between text-[11px] text-white/40">
                        <Link to="/forgot-password" className="hover:text-gold-400">Forgot password?</Link>
                        <span>Not an admin? <Link to="/login" className="text-gold-400 hover:text-gold-200 font-bold">User login →</Link></span>
                    </div>
                </form>

                <p className="text-center text-[10px] text-white/30 mt-6 uppercase tracking-widest">
                    All access is logged · unauthorised attempts prohibited
                </p>
            </div>
        </div>
    );
}
